import { FC, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { Member } from '../types/member';
import { FinancialHealthStats } from '../types/financialStats';
// NEW: Use secure API service instead of direct Supabase queries
import { getMembers as getMembersFromApi, mapLenderMemberToMemberWithScore } from '../services/lenderApiService';
import { useDataSource } from '../contexts/DataSourceContext';
import { motion } from 'framer-motion';
import { FiFilter, FiArrowLeft } from 'react-icons/fi';
// Audit traceability: track which members/scores lender has viewed
import { recordMembersDisplayed } from '../utils/lenderSessionTracking';
// import { format, parseISO } from 'date-fns'; // Used by formatMonthForDisplay (commented out)

// Component imports
import MemberCard from '../components/dashboard/MemberCard';
import AssistModal from '../components/dashboard/AssistModal';
import EmailModal from '../components/dashboard/EmailModal';
import TopFilterBar from '../components/dashboard/TopFilterBar';
import SideFilterPanel, { VeraScoreRange, QuickSegment, VERASCORE_RANGES } from '../components/dashboard/SideFilterPanel';
import { useAppConfig } from '../context/AppConfigContext';
import useAppState from '../common/AppState';
import ResponsiveChart from '../components/charts/ResponsiveChart';
import { VeraScoreChart } from '../components/charts/VeraScoreChart';

interface MemberListPageProps {
  isFilterPanelOpen: boolean;
  onFilterToggle: () => void;
  updateActiveFilters?: (active: boolean) => void;
}

// Types for modal state management
interface ModalState {
  isOpen: boolean;
  memberId?: string;
  memberEmail?: string;
  memberName?: string;
}

interface AssistModalState {
  isOpen: boolean;
  member?: Member;
}

interface MemberWithScore extends Member {
  // Phase 9G: nullable — null means no official score exists
  currentScore?: number | null;
  financialData?: FinancialHealthStats[];
}

// Generate a unique instance ID for this component to track remounts
let instanceCounter = 0;

const MemberListPage: FC<MemberListPageProps> = ({ isFilterPanelOpen, onFilterToggle, updateActiveFilters }) => {
  // Track this specific component instance
  const instanceId = useRef(`MLP-${++instanceCounter}`);

  if (import.meta.env.DEV) console.log(`🏠 [MemberListPage][${instanceId.current}] Component RENDER at ${new Date().toISOString()}`);

  // Get auth state to guard API calls
  const { isAuthenticated } = useAppState();

  // Track mount/unmount lifecycle
  useEffect(() => {
    if (import.meta.env.DEV) console.log(`🟢 [MemberListPage][${instanceId.current}] MOUNTED`);
    return () => {
      if (import.meta.env.DEV) console.log(`🔴 [MemberListPage][${instanceId.current}] UNMOUNTED`);
    };
  }, []);

  // Note: org_id filtering now handled server-side by API (Epic 0.9.5)

  // Data state
  const [members, setMembers] = useState<MemberWithScore[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalMembers, setTotalMembers] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;
  const { dataSource } = useDataSource();

  // Selected member for detail view (null = show list view)
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const selectedMember = useMemo(() =>
    members.find(m => m.uuid === selectedMemberId) || null,
    [members, selectedMemberId]
  );

  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: [] as string[],
    sortBy: 'verascore',
    sortOrder: 'desc' as 'asc' | 'desc',
    showLcuOnly: true,
    veraScoreRange: [] as VeraScoreRange[],
    hasDashboardData: null as boolean | null,
    quickSegment: 'none' as QuickSegment
  });

  // Track if this is a search operation
  const isSearchOperation = useRef(false);

  // Global month selection state (Option 3: Hybrid Date Selection)
  // NOTE: Global Month Dropdown UI is currently DISABLED (see comment in JSX below)
  // The state remains for effectiveGlobalMonth computation which defaults to most recent data month
  // Default to null, which means "use latest month from data"
  const [globalMonth, _setGlobalMonth] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isMonthDropdownOpen, _setIsMonthDropdownOpen] = useState(false); // Kept for potential re-enablement
  const monthDropdownRef = useRef<HTMLDivElement>(null); // Kept for potential re-enablement

  // Compute available months from all members' financial data
  const availableMonths = useMemo(() => {
    const monthSet = new Set<string>();
    members.forEach(member => {
      if (member.financialData) {
        member.financialData.forEach(stat => {
          if (stat.month) {
            monthSet.add(stat.month);
          }
        });
      }
    });
    // Sort descending (most recent first)
    return Array.from(monthSet).sort((a, b) => b.localeCompare(a));
  }, [members]);

  // Effective global month: if not set, use the most recent available month
  const effectiveGlobalMonth = useMemo(() => {
    const result = globalMonth ? globalMonth : (availableMonths.length > 0 ? availableMonths[0] : null);
    if (import.meta.env.DEV) console.log('🗓️ [MemberListPage] effectiveGlobalMonth computed:', {
      globalMonth,
      availableMonthsFirst: availableMonths[0],
      result
    });
    return result;
  }, [globalMonth, availableMonths]);

  // Format month for display (e.g., "2025-12" -> "December 2025")
  // NOTE: Used in the commented-out Global Month Dropdown JSX below.
  // Uncomment when re-enabling the dropdown.
  // const formatMonthForDisplay = useCallback((month: string | null): string => {
  //   if (!month) return 'Latest';
  //   try {
  //     const date = parseISO(month.length === 7 ? `${month}-01` : month);
  //     return format(date, 'MMMM yyyy');
  //   } catch {
  //     return month;
  //   }
  // }, []);

  // Close month dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target as Node)) {
        _setIsMonthDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Modal state
  const [assistModal, setAssistModal] = useState<AssistModalState>({ isOpen: false });
  const [emailModal, setEmailModal] = useState<ModalState>({ isOpen: false });

  // Refs for infinite scroll
  const mainContentRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver>();
  const scrollPositionRef = useRef<number>(0);
  const fetchInProgressRef = useRef<boolean>(false);
  const hasFetchedRef = useRef<boolean>(false);
  const { filterPosition, setFilterPosition } = useAppConfig();
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);

  // Added state for highlighted member
  const [highlightedMemberId, setHighlightedMemberId] = useState<string | null>(null);
  const memberCardRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  // Add state to track if the user came from the insights view
  const [cameFromInsights, setCameFromInsights] = useState(false);

  // Update isLargeScreen on window resize and set filter position accordingly
  useEffect(() => {
    const handleResize = () => {
      const largeScreen = window.innerWidth >= 1024;
      setIsLargeScreen(largeScreen);
      
      // Automatically switch to appropriate filter position based on screen size
      if (largeScreen && filterPosition === 'top') {
        // On desktop, use the sidebar
        setFilterPosition('left');
      } else if (!largeScreen && filterPosition === 'left') {
        // On mobile, use the top bar
        setFilterPosition('top');
      }
    };

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Initial check on component mount
    handleResize();
    
    // Clean up listener
    return () => window.removeEventListener('resize', handleResize);
  }, [filterPosition, setFilterPosition]);

  // Fetch members using secure API (Epic 0.9.5)
  const fetchMembers = useCallback(async (page: number, _isNewSearch = false) => {
    if (import.meta.env.DEV) console.log('🚀 [MemberListPage] fetchMembers called, page:', page, 'isAuthenticated:', isAuthenticated);

    // Guard: Don't fetch if not authenticated yet
    if (isAuthenticated !== 'yes') {
      if (import.meta.env.DEV) console.log('⏸️ [MemberListPage] Skipping fetch - not authenticated yet');
      return;
    }

    // Guard: Don't start a new fetch if one is already in progress
    if (fetchInProgressRef.current) {
      if (import.meta.env.DEV) console.log('⏸️ [MemberListPage] Skipping fetch - another fetch is in progress');
      return;
    }

    // Guard: For page 1, don't re-fetch if we already have data (prevents double-fetch on remount)
    // BUT: always re-fetch when a search/filter operation is in progress
    if (page === 1 && hasFetchedRef.current && members.length > 0 && !isSearchOperation.current) {
      if (import.meta.env.DEV) console.log('⏸️ [MemberListPage] Skipping fetch - already have data from previous fetch');
      setInitialLoading(false);
      return;
    }

    try {
      fetchInProgressRef.current = true;

      // For subsequent pages, we don't show a loading state to avoid UI jumps
      if (page > 1) {
        setLoadingMore(true);
      } else {
        setInitialLoading(true);
      }

      // Save scroll position before loading
      if (mainContentRef.current) {
        scrollPositionRef.current = mainContentRef.current.scrollTop;
      }

      // NEW: Use secure API endpoint instead of direct Supabase queries
      // API returns all data in one call (no N+1 query problem!)
      const fetchId = Math.random().toString(36).substr(2, 6);
      if (import.meta.env.DEV) console.log(`📡 [MemberListPage][${fetchId}] Fetching members via API...`);
      if (import.meta.env.DEV) console.log(`📡 [MemberListPage][${fetchId}] getMembersFromApi function:`, typeof getMembersFromApi, getMembersFromApi?.name);

      let lenderMembers;
      try {
        lenderMembers = await getMembersFromApi();
      } catch (apiError) {
        if (import.meta.env.DEV) console.error(`❌ [MemberListPage][${fetchId}] getMembersFromApi threw error:`, apiError);
        throw apiError;
      }
      if (import.meta.env.DEV) console.log(`📥 [MemberListPage][${fetchId}] API returned:`, lenderMembers);
      if (import.meta.env.DEV) console.log(`📥 [MemberListPage][${fetchId}] Type:`, typeof lenderMembers, Array.isArray(lenderMembers));
      if (import.meta.env.DEV) console.log(`📥 [MemberListPage][${fetchId}] Length:`, lenderMembers?.length, 'members');

      // FALLBACK: If API returned undefined but we have cached data, use it
      // This works around a mysterious issue where getMembersFromApi returns undefined
      // even though lenderApiService correctly cached the data
      if (!lenderMembers || !Array.isArray(lenderMembers)) {
        const windowCache = (window as Window & { __lenderApiMembersCache?: { members: unknown[] | null } }).__lenderApiMembersCache;
        if (windowCache?.members && Array.isArray(windowCache.members) && windowCache.members.length > 0) {
          if (import.meta.env.DEV) console.warn(`⚠️ [MemberListPage][${fetchId}] API returned invalid data, but found ${windowCache.members.length} cached members - using cache`);
          lenderMembers = windowCache.members;
        }
      }

      // Defensive: ensure we have an array
      if (!lenderMembers || !Array.isArray(lenderMembers)) {
        if (import.meta.env.DEV) console.error(`❌ [MemberListPage][${fetchId}] Invalid response AND no cache - expected array, got:`, lenderMembers);
        setError('Failed to load members - invalid response');
        setMembers([]);
        return;
      }

      // Map API response to app's member format
      const membersWithScores = lenderMembers.map(lm => {
        const mapped = mapLenderMemberToMemberWithScore(lm);
        if (import.meta.env.DEV) console.log(`[DEBUG] Member ${mapped.member_name} score: ${mapped.currentScore}, uuid: ${mapped.uuid}`);
        return mapped;
      });
      if (import.meta.env.DEV) console.log('🔄 [MemberListPage] Mapped members:', membersWithScores.length);

      // Audit traceability: Record all members displayed with their run_ids
      recordMembersDisplayed(membersWithScores);

      // Apply client-side filtering (API already filters by org)
      let filteredMembers = membersWithScores;

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredMembers = filteredMembers.filter(m =>
          m.member_name.toLowerCase().includes(searchLower) ||
          m.email.toLowerCase().includes(searchLower)
        );
      }

      // Status filter
      if (filters.status.length > 0) {
        filteredMembers = filteredMembers.filter(m =>
          filters.status.includes(m.dashboard_status)
        );
      }

      // Sort. WP-8 Slice 8C: members without a renderable current score
      // (null/undefined currentScore — e.g., held, unavailable, no official
      // score) always sort to the bottom regardless of asc/desc so they
      // never rank as if they scored 0.
      filteredMembers.sort((a, b) => {
        if (filters.sortBy === 'verascore') {
          const aNull = a.currentScore == null;
          const bNull = b.currentScore == null;
          if (aNull && bNull) return 0;
          if (aNull) return 1;
          if (bNull) return -1;
          return filters.sortOrder === 'asc'
            ? (a.currentScore as number) - (b.currentScore as number)
            : (b.currentScore as number) - (a.currentScore as number);
        }

        let aVal: number | string = 0;
        let bVal: number | string = 0;

        if (filters.sortBy === 'name') {
          aVal = a.member_name;
          bVal = b.member_name;
        } else if (filters.sortBy === 'income') {
          aVal = a.yearly_gross_income ?? 0;
          bVal = b.yearly_gross_income ?? 0;
        }

        if (typeof aVal === 'string') {
          return filters.sortOrder === 'asc'
            ? aVal.localeCompare(bVal as string)
            : (bVal as string).localeCompare(aVal);
        }
        return filters.sortOrder === 'asc' ? aVal - (bVal as number) : (bVal as number) - aVal;
      });

      // Pagination (client-side for now)
      const startIdx = (page - 1) * pageSize;
      const paginatedMembers = filteredMembers.slice(startIdx, startIdx + pageSize);

      setTotalMembers(filteredMembers.length);
      setHasMore(startIdx + pageSize < filteredMembers.length);

      if (import.meta.env.DEV) console.log(`✅ [MemberListPage] Loaded ${paginatedMembers.length} of ${filteredMembers.length} members`);
      if (import.meta.env.DEV) console.log('📋 [MemberListPage] Paginated members to set:', JSON.stringify(paginatedMembers.map(m => ({ uuid: m.uuid, name: m.member_name })), null, 2));

      // If it's the first page, replace members; otherwise append
      if (page === 1) {
        if (import.meta.env.DEV) console.log('📝 [MemberListPage] Setting members state (page 1)');
        setMembers(paginatedMembers);
        // Reset scroll position for new searches
        if (mainContentRef.current) {
          mainContentRef.current.scrollTop = 0;
        }
      } else {
        // Append new members, but prevent duplicates
        setMembers(prev => {
          // Get existing member UUIDs
          const existingMemberIds = new Set(prev.map(m => m.uuid));
          // Filter out members that already exist
          const newMembers = paginatedMembers.filter(m => !existingMemberIds.has(m.uuid));
          // Return combined array
          return [...prev, ...newMembers];
        });
        // Restore scroll position after DOM update
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPositionRef.current);
        });
      }

      setError(null);
    } catch (e) {
      if (import.meta.env.DEV) console.error('❌ [MemberListPage] Error loading members:', e);
      if (import.meta.env.DEV) console.error('❌ [MemberListPage] Error details:', e instanceof Error ? e.stack : String(e));
      setError(e instanceof Error ? e.message : 'An error occurred');
      setMembers([]);
    } finally {
      if (import.meta.env.DEV) console.log('🏁 [MemberListPage] fetchMembers completed, setting initialLoading=false');
      fetchInProgressRef.current = false;
      hasFetchedRef.current = true;
      setInitialLoading(false);
      setLoadingMore(false);
      setSearchLoading(false);
      isSearchOperation.current = false;
    }
  }, [filters, pageSize, isAuthenticated, members.length]); // Note: orgId no longer needed - API handles org filtering via session

  // Fetch members when page, filters change, or when user becomes authenticated
  useEffect(() => {
    if (import.meta.env.DEV) console.log('🔄 [MemberListPage] useEffect triggered - isAuthenticated:', isAuthenticated);
    // Only fetch if authenticated
    if (isAuthenticated === 'yes') {
      if (import.meta.env.DEV) console.log('✅ [MemberListPage] User authenticated - calling fetchMembers');
      fetchMembers(currentPage);
    } else {
      if (import.meta.env.DEV) console.log('⏸️ [MemberListPage] Not authenticated yet - skipping fetch');
    }
  }, [currentPage, filters, dataSource, fetchMembers, isAuthenticated]);

  // Refetch data when data source changes
  useEffect(() => {
    if (import.meta.env.DEV) console.log('Data source changed to:', dataSource);
    setCurrentPage(1);
    setMembers([]);
    // Only show loading if authenticated, otherwise just clear
    if (isAuthenticated === 'yes') {
      setInitialLoading(true);
    }
  }, [dataSource, isAuthenticated]);

  // Save scroll position on scroll
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const lastMemberElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadingMore) return;

      // Disconnect previous observer
      if (observer.current) observer.current.disconnect();

      // Don't observe if there are no more pages
      if (!hasMore) return;

      // Create new observer
      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasMore && !loadingMore) {
            setLoadingMore(true);
            setCurrentPage(prevPage => prevPage + 1);
          }
        },
        { threshold: 0.5 }
      );

      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore]
  );

  // Handle filter changes
  const handleFilterChange = (newFilters: typeof filters) => {
    // If filters are changing, we're loading a new set of data
    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      isSearchOperation.current = true; // Bypass hasFetchedRef guard
      setInitialLoading(true);
      // Reset to page 1 for new filters
      setCurrentPage(1);
      setFilters(newFilters);
    }
  };

  // Handle search submit
  const handleSearchSubmit = () => {
    if (searchInput !== filters.search) {
      isSearchOperation.current = true;
      setSearchLoading(true);
      setCurrentPage(1);
      setFilters(prev => ({ ...prev, search: searchInput }));
    }
  };

  // Helper function to check if a string contains non-English characters
  const hasNonEnglishChars = (str: string): boolean => {
    return !/^[a-z\s\-'\.]+$/i.test(str);
  };

  // Helper function for natural sorting of email addresses
  const naturalSortEmails = (a: string, b: string): number => {
    // Split the email into parts (local part and domain)
    const [aLocal = '', aDomain = ''] = a.split('@');
    const [bLocal = '', bDomain = ''] = b.split('@');
    
    // Compare domains first
    if (aDomain !== bDomain) {
      return aDomain.localeCompare(bDomain);
    }
    
    // If domains are the same, compare local parts with natural sorting
    // This regex splits strings into chunks of numbers and non-numbers
    const chunkRegex = /(\d+|\D+)/g;
    
    const aChunks = aLocal.match(chunkRegex) || [];
    const bChunks = bLocal.match(chunkRegex) || [];
    
    const minLength = Math.min(aChunks.length, bChunks.length);
    
    // Compare each chunk
    for (let i = 0; i < minLength; i++) {
      const aChunk = aChunks[i];
      const bChunk = bChunks[i];
      
      // If both chunks are numbers, compare them as numbers
      const aIsNum = /^\d+$/.test(aChunk);
      const bIsNum = /^\d+$/.test(bChunk);
      
      if (aIsNum && bIsNum) {
        const diff = parseInt(aChunk, 10) - parseInt(bChunk, 10);
        if (diff !== 0) return diff;
      } else {
        // Otherwise, compare as strings
        const diff = aChunk.localeCompare(bChunk);
        if (diff !== 0) return diff;
      }
    }
    
    // If all chunks compared so far are equal, the shorter one comes first
    return aChunks.length - bChunks.length;
  };

  // Helper function to check if a member matches the VeraScore range filter
  const matchesVeraScoreRange = useCallback((score: number | null | undefined, ranges: VeraScoreRange[]): boolean => {
    if (ranges.length === 0) return true; // No filter applied
    if (score === undefined || score === null) return false;

    return ranges.some(rangeValue => {
      const range = VERASCORE_RANGES.find(r => r.value === rangeValue);
      if (!range) return false;
      return score >= range.min && score <= range.max;
    });
  }, []);

  // Helper function to check if a member has dashboard data
  const hasDashboardDataCheck = useCallback((member: MemberWithScore): boolean => {
    // A member has dashboard data if they have financial data with a verascore
    return !!(member.financialData && member.financialData.length > 0 && member.currentScore !== undefined);
  }, []);

  // Client-side filtering and sorting for verascore since it's not a database column
  // Also handles additional sorting for non-English characters and email natural sorting
  const sortedMembers = useMemo(() => {
    // First deduplicate members by UUID
    let filteredMembers = [...members].reduce((acc: MemberWithScore[], current) => {
      const isDuplicate = acc.find(m => m.uuid === current.uuid);
      if (!isDuplicate) {
        acc.push(current);
      }
      return acc;
    }, []);

    // Apply VeraScore range filter (client-side)
    if (filters.veraScoreRange.length > 0) {
      filteredMembers = filteredMembers.filter(member =>
        matchesVeraScoreRange(member.currentScore, filters.veraScoreRange)
      );
    }

    // Apply hasDashboardData filter (client-side)
    if (filters.hasDashboardData !== null) {
      filteredMembers = filteredMembers.filter(member => {
        const hasData = hasDashboardDataCheck(member);
        return filters.hasDashboardData ? hasData : !hasData;
      });
    }

    // Apply quick segment filters (client-side)
    if (filters.quickSegment !== 'none') {
      switch (filters.quickSegment) {
        case 'loan_ready':
          // High score (80+) and has complete data
          filteredMembers = filteredMembers.filter(member =>
            member.currentScore != null &&
            member.currentScore >= 80 &&
            hasDashboardDataCheck(member)
          );
          break;
        case 'needs_attention':
          // Low score (<60) or no data
          filteredMembers = filteredMembers.filter(member =>
            !hasDashboardDataCheck(member) ||
            (member.currentScore != null && member.currentScore < 60)
          );
          break;
        case 'new_members':
          // Members with no financial data yet (new to the system)
          filteredMembers = filteredMembers.filter(member =>
            !member.financialData || member.financialData.length === 0
          );
          break;
      }
    }

    // Special case for verascore sorting which isn't handled on the server.
    // WP-8 Slice 8C: members with no renderable current score (null) always
    // sort to the bottom regardless of asc/desc to avoid ranking them as if
    // they scored 0.
    if (filters.sortBy === 'verascore') {
      return [...filteredMembers].sort((a, b) => {
        const scoreA = (a as MemberWithScore).currentScore;
        const scoreB = (b as MemberWithScore).currentScore;
        const aNull = scoreA == null;
        const bNull = scoreB == null;
        if (aNull && bNull) return 0;
        if (aNull) return 1;
        if (bNull) return -1;
        return filters.sortOrder === 'asc'
          ? (scoreA as number) - (scoreB as number)
          : (scoreB as number) - (scoreA as number);
      });
    }

    // Natural sorting for email addresses
    if (filters.sortBy === 'email') {
      return [...filteredMembers].sort((a, b) => {
        const result = naturalSortEmails(a.email, b.email);
        return filters.sortOrder === 'asc' ? result : -result;
      });
    }

    // Additional sorting for last_name to handle non-English characters
    if (filters.sortBy === 'member_name') {
      return [...filteredMembers].sort((a, b) => {
        // Check if either name has non-English characters
        const aHasNonEnglish = hasNonEnglishChars(a.last_name + a.first_name);
        const bHasNonEnglish = hasNonEnglishChars(b.last_name + b.first_name);

        // If one has non-English and the other doesn't, the English one comes first
        if (aHasNonEnglish && !bHasNonEnglish) return filters.sortOrder === 'asc' ? 1 : -1;
        if (!aHasNonEnglish && bHasNonEnglish) return filters.sortOrder === 'asc' ? -1 : 1;

        // If both are the same type (both English or both non-English)
        // Use the server-provided order which is already sorted by last_name then first_name
        return 0;
      });
    }

    // For all other sorts, the server already handled it
    return filteredMembers;
  }, [members, filters.sortBy, filters.sortOrder, filters.veraScoreRange, filters.hasDashboardData, filters.quickSegment, matchesVeraScoreRange, hasDashboardDataCheck]);

  // Modal handlers
  const openAssistModal = (memberId: string) => {
    const member = members.find(m => m.uuid === memberId);
    if (member) {
      setAssistModal({ isOpen: true, member });
    }
  };

  const closeAssistModal = () => {
    setAssistModal({ isOpen: false });
  };

  const handleStatusUpdate = async () => {
    // Refresh the members list to show updated status
    await fetchMembers(1);
  };

  const openEmailModal = (memberId: string) => {
    const member = members.find(m => m.uuid === memberId);
    setEmailModal({ 
      isOpen: true, 
      memberId,
      memberEmail: member?.email,
      memberName: member?.first_name || member?.member_name
    });
  };

  const closeEmailModal = () => {
    setEmailModal({ isOpen: false });
  };

  // Track window width for responsive behavior
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Handle scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 300px from the top
      setShowScrollToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to scroll back to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Notify parent component about active filters
  useEffect(() => {
    const hasActiveFilters = filters.search !== '' ||
      filters.status.length > 0 ||
      filters.veraScoreRange.length > 0 ||
      filters.hasDashboardData !== null ||
      filters.quickSegment !== 'none';
    if (updateActiveFilters) {
      updateActiveFilters(hasActiveFilters);
    }
  }, [filters, updateActiveFilters]);

  // Check URL parameters for memberId and referrer on mount and when URL changes
  useEffect(() => {
    const handleURLParamChange = () => {
      const params = new URLSearchParams(window.location.search);
      const memberId = params.get('memberId');
      const fromInsights = params.get('from') === 'insights';
      
      setCameFromInsights(fromInsights);
      
      if (memberId) {
        if (import.meta.env.DEV) console.log('Highlighting member:', memberId);
        setHighlightedMemberId(memberId);
        
        // If we already have the members loaded, scroll to the member immediately
        setTimeout(() => {
          scrollToMember(memberId);
        }, 500);
      } else {
        setHighlightedMemberId(null);
      }
    };
    
    // Initial check
    handleURLParamChange();
    
    // Listen for URL changes
    window.addEventListener('popstate', handleURLParamChange);
    
    return () => {
      window.removeEventListener('popstate', handleURLParamChange);
    };
  }, []);
  
  // Function to scroll to a specific member by ID
  const scrollToMember = useCallback((memberId: string) => {
    if (import.meta.env.DEV) console.log('Attempting to scroll to member:', memberId);
    
    const memberElement = memberCardRefs.current[memberId];
    if (memberElement) {
      if (import.meta.env.DEV) console.log('Found member element, scrolling to it');
      memberElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      if (import.meta.env.DEV) console.log('Member element not found in refs');
      // If the member isn't loaded yet, we may need to fetch more data
      // Check if the member ID is in the current members array
      const memberInCurrentList = members.some(m => m.uuid === memberId);
      
      if (!memberInCurrentList && hasMore) {
        if (import.meta.env.DEV) console.log('Member not in current list, loading more data');
        // Load more members to try to find the one we're looking for
        fetchMembers(currentPage + 1);
      }
    }
  }, [members, hasMore, currentPage, fetchMembers]);
  
  // When members change and we have a highlighted member, try to scroll to it
  useEffect(() => {
    if (highlightedMemberId && members.length > 0) {
      const memberInList = members.some(m => m.uuid === highlightedMemberId);
      if (memberInList) {
        if (import.meta.env.DEV) console.log('Member found in list, scrolling to it');
        setTimeout(() => scrollToMember(highlightedMemberId), 300); // Increased delay for smoother transition
      }
    }
  }, [members, highlightedMemberId, scrollToMember]);

  // Function to navigate back to insights view
  const navigateToInsights = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Use history.pushState to update URL without page reload
    const url = new URL(window.location.href);
    url.searchParams.set('view', 'insights');
    url.searchParams.delete('memberId');
    url.searchParams.delete('from');
    window.history.pushState({}, '', url.toString());
    
    // Dispatch a custom event to notify App component of the change
    window.dispatchEvent(new Event('popstate'));
  };

  return (
    <div className="relative min-h-screen bg-slate-50" style={{
      overflowX: 'clip', // Use clip instead of hidden to prevent scroll container creation
      background: 'linear-gradient(135deg, #f0f4fa, #e8eef7, #f1f5fc)',
      backgroundSize: '600% 600%',
      backgroundAttachment: 'fixed'
    }}>
      {/* Page title and description */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-2 px-3 sm:px-6 py-4 sm:py-6 pb-4"
        style={{
          background: 'rgba(230, 235, 242, 0.7)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-700">Member Details</h1>
            </div>
            <button
              onClick={onFilterToggle}
              className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-white rounded-full border border-slate-200 text-sm text-slate-600 hover:text-slate-800 transition-colors shadow-sm"
              aria-expanded={isFilterPanelOpen}
              aria-haspopup="true"
            >
              <FiFilter size={16} />
              <span className="hidden xs:inline">Filter Members</span>
              {(filters.search !== '' || filters.status.length > 0 || 
                filters.sortBy !== 'verascore' || filters.sortOrder !== 'desc' || 
                filters.showLcuOnly !== true) && (
                <span className="bg-brand-blue text-white text-xs px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </button>
          </div>
          <p className="text-slate-500 text-sm sm:text-base">
            Evaluate member financial health and recent activity to provide personalized guidance and better serve their needs
          </p>
        </div>
      </motion.div>

      {/* Filter Panel */}
      {filterPosition === 'left' && (
        <>
          {/* Semi-transparent overlay for mobile/tablet only */}
          {isFilterPanelOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-20 z-20 lg:hidden transition-opacity duration-300 ease-in-out"
              onClick={onFilterToggle}
              aria-hidden="true"
            />
          )}

          <aside
            className={`
              fixed z-30
              transition-all duration-300 ease-in-out
              ${
                isFilterPanelOpen
                  ? 'w-4/5 sm:w-72 md:w-60 translate-x-0'
                  : 'w-0 -translate-x-full opacity-0 pointer-events-none'
              }
              top-[4rem] lg:top-[200px] left-0
            `}
            style={{
              borderRadius: '0 12px 12px 0',
              overflow: 'hidden',
              boxShadow: isFilterPanelOpen ? '0 10px 25px -5px rgba(0, 0, 0, 0.1)' : 'none',
              height: 'auto',
              maxHeight: 'calc(100vh - 4.5rem)'
            }}
          >
            <SideFilterPanel
              filters={filters}
              searchInput={searchInput}
              onFilterChange={handleFilterChange}
              onSearchInputChange={setSearchInput}
              onSearchSubmit={handleSearchSubmit}
              totalCount={totalMembers}
              filteredCount={sortedMembers.length}
              isOpen={isFilterPanelOpen}
              isSearching={searchLoading}
              onClose={onFilterToggle}
            />
          </aside>
        </>
      )}

      {/* Main Content with Sidebar Layout */}
      <div className="flex flex-1 relative">
        {/* Conditional overlay when coming from insights and a member is highlighted */}
        {cameFromInsights && highlightedMemberId && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 pointer-events-none">
            {/* The Back to Insights button will now be placed by each member card */}
          </div>
        )}

        {/* Main Content */}
        <main
          ref={mainContentRef}
          className={`
            flex-1 w-full px-2 sm:px-4 relative
            ${filterPosition === 'left' && isFilterPanelOpen && isLargeScreen ? 'lg:pl-60' : ''}
          `}
          style={{
            marginTop: `0px`,
            transition: 'padding-left 0.3s ease-in-out, margin-top 0.3s ease-in-out',
            zIndex: cameFromInsights && highlightedMemberId ? 41 : 'auto' // Ensure content is above the dimmed overlay
          }}
        >
          {/* Top Filter Bar - Made sticky with animation */}
          {filterPosition === 'top' && (
            <div 
              className={`
                fixed left-0 right-0 border-b border-slate-200 shadow-sm
                transform transition-all duration-300 ease-in-out
                ${isFilterPanelOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
                top-[4rem] lg:top-[200px]
                z-30
              `}
            >
              {/* Semi-transparent overlay for mobile/tablet only - similar to side filter */}
              {isFilterPanelOpen && !isLargeScreen && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-20 z-20 transition-opacity duration-300 ease-in-out"
                  onClick={onFilterToggle}
                  aria-hidden="true"
                />
              )}
              
              {/* Content */}
              <div className="max-w-7xl mx-auto px-3 sm:px-4">
                <TopFilterBar
                  filters={filters}
                  searchInput={searchInput}
                  onFilterChange={handleFilterChange}
                  onSearchInputChange={setSearchInput}
                  onSearchSubmit={handleSearchSubmit}
                  totalCount={totalMembers}
                  filteredCount={sortedMembers.length}
                  isSearching={searchLoading}
                  isOpen={isFilterPanelOpen}
                  onClose={onFilterToggle}
                />
              </div>
            </div>
          )}

          {/* Main content with adjusted padding when filter is open */}
          <div 
            className={`
              max-w-7xl mx-auto
              transition-all duration-300 ease-in-out
              ${filterPosition === 'top' && isFilterPanelOpen ? 'pt-32 sm:pt-28' : 'pt-2'}
              p-3 sm:p-6
              bg-opacity-70 bg-[rgba(230,235,242,0.7)]
              backdrop-blur-md
              rounded-xl
              border border-white/30
              shadow-lg
              ${cameFromInsights && highlightedMemberId ? 'relative' : ''}
            `}
          >
            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
                <p className="font-medium">Error loading members</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/*
              GLOBAL MONTH SELECTOR - COMMENTED OUT (2025-01-24)

              BUSINESS DECISION: The Global Date Dropdown has been disabled for the following reasons:

              1. DEFAULT BEHAVIOR: The Lender Dashboard should always default to the most recent month
                 for which we have complete financial data. This is typically the previous calendar month
                 once we're past the 15th of the current month (allowing time for data processing).

              2. PER-CARD SELECTION: Each member card already has its own month selector when expanded,
                 allowing lenders to drill into historical data for specific members without affecting
                 the view of other members.

              3. REDUCED COMPLEXITY: A global selector adds UI complexity without clear value-add.
                 Lenders primarily want to see the latest verified data, not switch all cards at once.

              4. DATA INTEGRITY: By defaulting to the most recent complete data month, we ensure
                 lenders always see fully processed and verified financial information.

              The effectiveGlobalMonth logic remains in place to compute the default month from
              availableMonths[0] (most recent month with data). Individual MemberCards receive this
              as their globalMonth prop and use it as the default display month.

              To re-enable: Uncomment the JSX block below and the related state/refs at the top of the file.

              See: Audit Framework Documentation for full rationale.
            */}
            {/*
            {!initialLoading && sortedMembers.length > 0 && availableMonths.length > 1 && (
              <div className="flex items-center justify-between mb-4 px-1">
                <div className="flex items-center gap-2">
                  <FiCalendar className="text-slate-500" size={16} />
                  <span className="text-sm text-slate-600">Viewing data for:</span>
                  <div className="relative" ref={monthDropdownRef}>
                    <button
                      onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      {formatMonthForDisplay(effectiveGlobalMonth)}
                      <FiChevronDown
                        className={`text-slate-400 transition-transform ${isMonthDropdownOpen ? 'rotate-180' : ''}`}
                        size={14}
                      />
                    </button>
                    {isMonthDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                        {availableMonths.map((month) => (
                          <button
                            key={month}
                            onClick={() => {
                              if (import.meta.env.DEV) console.log('🗓️ [MonthDropdown] Selected month:', month);
                              if (import.meta.env.DEV) console.log('🗓️ [MonthDropdown] Previous globalMonth:', globalMonth);
                              setGlobalMonth(month);
                              setIsMonthDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                              month === effectiveGlobalMonth ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'
                            }`}
                          >
                            {formatMonthForDisplay(month)}
                            {month === availableMonths[0] && (
                              <span className="ml-2 text-xs text-slate-400">(Latest)</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {globalMonth && (
                  <button
                    onClick={() => setGlobalMonth(null)}
                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Reset to Latest
                  </button>
                )}
              </div>
            )}
            */}

            {/* Loading State - Initial Load */}
            {initialLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-12 h-12 border-4 border-slate-200 border-t-brand-blue rounded-full animate-spin mb-4"></div>
                <p className="text-slate-600">Loading members...</p>
              </div>
            ) : sortedMembers.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-8 text-center">
                <p className="text-slate-600 mb-4">No members found matching your criteria.</p>
                <button
                  onClick={() => {
                    setFilters({
                      search: '',
                      status: [],
                      sortBy: 'verascore',
                      sortOrder: 'desc',
                      showLcuOnly: true,
                      veraScoreRange: [],
                      hasDashboardData: null,
                      quickSegment: 'none'
                    });
                    setSearchInput('');
                  }}
                  className="px-4 py-2 bg-brand-blue text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {/* Show Detail View if a member is selected */}
                {selectedMember ? (
                  <div className="space-y-4">
                    {/* Back to List Button */}
                    <button
                      onClick={() => setSelectedMemberId(null)}
                      className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <FiArrowLeft size={18} />
                      <span>Back to Member List</span>
                    </button>

                    {/* Detail Card */}
                    <MemberCard
                      member={selectedMember}
                      financialData={selectedMember.financialData || []}
                      globalMonth={effectiveGlobalMonth}
                      onAssist={() => openAssistModal(selectedMember.uuid)}
                      onEmail={() => openEmailModal(selectedMember.uuid)}
                    />
                  </div>
                ) : (
                  /* Member List Cards */
                  <div className="space-y-3">
                    {sortedMembers.map((member, index) => {
                      const isLastElement = index === sortedMembers.length - 1;
                      const isHighlighted = member.uuid === highlightedMemberId;

                      // If we're coming from insights and this member is highlighted,
                      // all other members should be visually de-emphasized
                      const shouldDim = cameFromInsights && highlightedMemberId && !isHighlighted;

                      return (
                        <div
                          key={member.uuid}
                          className="relative"
                        >
                          {/* Back to Insights button - only shown above highlighted member */}
                          {isHighlighted && cameFromInsights && (
                            <div className="absolute w-full flex justify-start pointer-events-auto z-[100]" style={{ top: '-62px' }}>
                              <button
                                onClick={navigateToInsights}
                                className="flex items-center gap-1 px-4 py-2 bg-white/90 hover:bg-white text-slate-700 text-sm rounded-md transition-colors border border-slate-200 shadow-md"
                                aria-label="Back to VS Insights"
                              >
                                <FiArrowLeft size={16} />
                                <span>Back to VS Insights</span>
                              </button>
                            </div>
                          )}

                          <div
                            ref={(el) => {
                              // Store ref for each member card
                              memberCardRefs.current[member.uuid] = el;

                              // Set last element ref for infinite scrolling
                              if (isLastElement) {
                                lastMemberElementRef(el);
                              }
                            }}
                            className={`
                              transition-all duration-300
                              ${isHighlighted ? 'ring-2 ring-brand-blue/70 shadow-lg' : ''}
                              ${shouldDim ? 'opacity-20 blur-[1px]' : 'opacity-100'}
                            `}
                          >
                            <MemberCard
                              member={member}
                              financialData={member.financialData || []}
                              globalMonth={effectiveGlobalMonth}
                              onAssist={() => openAssistModal(member.uuid)}
                              onEmail={() => openEmailModal(member.uuid)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Loading More Indicator */}
                {loadingMore && !initialLoading && (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-4 border-slate-200 border-t-brand-blue rounded-full animate-spin"></div>
                  </div>
                )}

                {/* End of list message */}
                {!initialLoading && !hasMore && sortedMembers.length > 0 && (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    End of member list • {sortedMembers.length} members shown
                  </div>
                )}

                {/* Hidden loading indicator for infinite scroll */}
                {loadingMore && !initialLoading && (
                  <span className="sr-only">Loading more members...</span>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Floating Back to Top Button */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-brand-blue text-white p-3 rounded-full shadow-lg hover:bg-blue-600 transition-all duration-300 z-20"
          aria-label="Back to top"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {/* Modals */}
      {assistModal.isOpen && assistModal.member && (
        <AssistModal
          member={assistModal.member}
          onClose={closeAssistModal}
          isOpen={assistModal.isOpen}
          onStatusUpdate={handleStatusUpdate}
        />
      )}

      {emailModal.isOpen && emailModal.memberId && (
        <EmailModal
          memberId={emailModal.memberId}
          memberEmail={emailModal.memberEmail}
          memberName={emailModal.memberName}
          onClose={closeEmailModal}
          isOpen={emailModal.isOpen}
        />
      )}

      {/* Mobile Chart Demo */}
      <div className="block md:hidden mb-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-2">VeraScore Trend</h2>
          <p className="text-sm text-gray-600 mb-4">
            Tap on the chart below to view it in fullscreen
          </p>
          <ResponsiveChart title="VeraScore Trend" height="160px">
            {members && members.length > 0 && members[0].financialData ? (
              <VeraScoreChart 
                data={members[0].financialData
                  .slice()
                  .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime())
                  .slice(-12)
                } 
                showDebug={false}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </ResponsiveChart>
        </div>
      </div>
    </div>
  );
};

export default MemberListPage;
